import { CircularProgress } from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers/auth/hooks";
import { useAllMetricDefinitions } from "@/shared/domain/metrics/hooks";

import { type Filter } from "./FilterSchema";
import { MetricFilterForm } from "./MetricFilterForm";
import styles from "./MetricsFilterMenu.module.css";
import { MetricTree } from "./MetricTree";

export interface MetricsFilterMenuProps {
  /** Fired when the user applies a metric filter. */
  onCommit: (filter: Filter) => void;
  /** Fired when the user cancels. */
  onCancel: () => void;
}

/**
 * The "Metrics" pane. Owns the data: fetches the org's metric definitions
 * (org-generic, so it belongs here rather than threaded through each consumer —
 * mirrors how `RoboqlFilterMenu` resolves its own org + data). Renders a fetch
 * spinner / error, or the pure two-column UI: searchable tree (`MetricTree`) +
 * threshold form (`MetricFilterForm`). Mounted only when the Metrics tab is
 * open, so the fetch is lazy.
 */
export function MetricsFilterMenu({
  onCommit,
  onCancel,
}: MetricsFilterMenuProps) {
  const { currentOrganization } = useAuth();
  // `string | undefined` (not `useCurrentOrgId`, which throws when no org
  // resolves): lets `useAllMetricDefinitions` self-gate on `enabled` and lets
  // this pane render its own loading/error UI before an org is available.
  const orgId = currentOrganization?.org_id;
  const { data, isPending, isError } = useAllMetricDefinitions(orgId);

  const names = React.useMemo(
    () => (data ?? []).map((definition) => definition.name),
    [data],
  );

  const [selectedMetric, setSelectedMetric] = React.useState<string | null>(
    null,
  );

  if (isPending) {
    return (
      <div className={styles.status} role="status" aria-label="Loading metrics">
        <CircularProgress size={20} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.status} role="status">
        Couldn’t load metrics.
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      <MetricTree
        names={names}
        selectedMetric={selectedMetric}
        onSelect={setSelectedMetric}
      />
      {selectedMetric === null ? (
        <div className={styles.emptyPanel}>
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>Pick a metric</p>
            <p className={styles.emptyBody}>
              Search or browse metrics on the left, then set a threshold to
              filter by.
            </p>
          </div>
        </div>
      ) : (
        <MetricFilterForm
          // Fresh form state per metric.
          key={selectedMetric}
          metric={selectedMetric}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}
